import * as XLSX from "xlsx";
import { LeaderboardEntry, LeaderboardTheme } from "../../types/home.types";

export interface ThemePreset {
  id: string;
  label: string;
  theme: LeaderboardTheme;
}

export const THEME_PRESETS: ThemePreset[] = [
  {
    id: "biomarker",
    label: "Know Your Biomarker",
    theme: {
      background: "#13317F",
      title: "#FFFFFF",
      text: "#FFFFFF",
      points: "#B3E2FF",
      accent: "#48A2E5",
      badgeFirst: "#FF8A3B",
      badgeFirstText: "#091840",
      badgeOther: "#0070CC",
      podiumFirstFrom: "#48A2E5",
      podiumFirstTo: "#0070CC",
      podiumSecondFrom: "#FF8A3B",
      podiumSecondTo: "#FF6B10",
      podiumThirdFrom: "#0070CC",
      podiumThirdTo: "#0D2260",
    },
  },
  {
    id: "light",
    label: "Hub Light",
    theme: {
      background: "#FFFFFF",
      title: "#13317F",
      text: "#1F2937",
      points: "#0070CC",
      accent: "#0070CC",
      badgeFirst: "#FF8A3B",
      badgeFirstText: "#FFFFFF",
      badgeOther: "#13317F",
      podiumFirstFrom: "#48A2E5",
      podiumFirstTo: "#0070CC",
      podiumSecondFrom: "#FFB27A",
      podiumSecondTo: "#FF8A3B",
      podiumThirdFrom: "#6B8FD6",
      podiumThirdTo: "#13317F",
    },
  },
  {
    id: "midnight",
    label: "Midnight",
    theme: {
      background: "#0B1020",
      title: "#FFFFFF",
      text: "#E5E7EB",
      points: "#A5B4FC",
      accent: "#818CF8",
      badgeFirst: "#FACC15",
      badgeFirstText: "#111827",
      badgeOther: "#6366F1",
      podiumFirstFrom: "#FBBF24",
      podiumFirstTo: "#B45309",
      podiumSecondFrom: "#818CF8",
      podiumSecondTo: "#6366F1",
      podiumThirdFrom: "#818CF8",
      podiumThirdTo: "#312E81",
    },
  },
  {
    id: "emerald",
    label: "Emerald",
    theme: {
      background: "#064E3B",
      title: "#FFFFFF",
      text: "#FFFFFF",
      points: "#A7F3D0",
      accent: "#34D399",
      badgeFirst: "#FBBF24",
      badgeFirstText: "#064E3B",
      badgeOther: "#059669",
      podiumFirstFrom: "#34D399",
      podiumFirstTo: "#047857",
      podiumSecondFrom: "#F59E0B",
      podiumSecondTo: "#B45309",
      podiumThirdFrom: "#10B981",
      podiumThirdTo: "#065F46",
    },
  },
];

export const DEFAULT_THEME = THEME_PRESETS[0].theme;

export const THEME_FIELDS: Array<{ key: keyof LeaderboardTheme; label: string }> = [
  { key: "background", label: "Background" },
  { key: "title", label: "Title" },
  { key: "text", label: "Names" },
  { key: "points", label: "Points" },
  { key: "accent", label: "Avatars" },
  { key: "badgeFirst", label: "1st place badge" },
  { key: "badgeFirstText", label: "1st place badge text" },
  { key: "badgeOther", label: "Other badges" },
  { key: "podiumFirstFrom", label: "1st place (from)" },
  { key: "podiumFirstTo", label: "1st place (to)" },
  { key: "podiumSecondFrom", label: "2nd place (from)" },
  { key: "podiumSecondTo", label: "2nd place (to)" },
  { key: "podiumThirdFrom", label: "3rd place (from)" },
  { key: "podiumThirdTo", label: "3rd place (to)" },
];

export interface RankedEntry extends LeaderboardEntry {
  rank: number;
}

/**
 * Highest points first; equal points share a rank and the next rank skips
 * ahead (1, 2, 2, 4).
 */
export function rankEntries<T extends LeaderboardEntry>(entries: T[]): Array<T & { rank: number }> {
  const sorted = [...entries].sort(
    (a, b) => b.points - a.points || a.name.localeCompare(b.name),
  );

  let rank = 0;
  return sorted.map((entry, index) => {
    if (index === 0 || entry.points !== sorted[index - 1].points) rank = index + 1;
    return { ...entry, rank };
  });
}

export const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const normalize = (value = "") => value.trim().replace(/\s+/g, " ").toLowerCase();

/** Same person: same email when both have one, otherwise same name. */
export function isSamePerson(a: LeaderboardEntry, b: LeaderboardEntry): boolean {
  if (a.email && b.email) return normalize(a.email) === normalize(b.email);
  return normalize(a.name) === normalize(b.name);
}

/**
 * Adds the incoming people to the current list. Someone already on it takes
 * the points from the file, and its email when it had none.
 */
export function mergeEntries<T extends LeaderboardEntry>(
  current: T[],
  incoming: LeaderboardEntry[],
  create: (entry: LeaderboardEntry) => T,
): { entries: T[]; added: number; updated: number } {
  const pending = [...incoming];
  let updated = 0;

  const entries = current.map((entry) => {
    const index = pending.findIndex((candidate) => isSamePerson(entry, candidate));
    if (index === -1) return entry;
    const [match] = pending.splice(index, 1);
    updated++;
    return { ...entry, points: match.points, email: entry.email || match.email };
  });

  const added = pending.map(create);
  return { entries: [...entries, ...added], added: added.length, updated };
}

export function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

export function withAlpha(hex: string, alpha: number): string {
  let value = hex.replace("#", "");
  if (value.length === 3) value = value.split("").map((c) => c + c).join("");
  const r = parseInt(value.slice(0, 2), 16);
  const g = parseInt(value.slice(2, 4), 16);
  const b = parseInt(value.slice(4, 6), 16);
  if ([r, g, b].some(Number.isNaN)) return hex;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/** Accepts 2815, "2,815", "2.815" and "2 815". */
export function parsePoints(value: unknown): number {
  if (typeof value === "number") return value;
  const text = String(value ?? "").trim().replace(/\s/g, "");
  if (text === "") return NaN;

  if (/^-?\d{1,3}(,\d{3})+(\.\d+)?$/.test(text)) return Number(text.replace(/,/g, ""));
  if (/^-?\d{1,3}(\.\d{3})+(,\d+)?$/.test(text)) {
    return Number(text.replace(/\./g, "").replace(",", "."));
  }
  return Number(text.replace(",", "."));
}

export interface ParsedSheet {
  headers: string[];
  rows: Record<string, unknown>[];
}

export async function readSpreadsheet(file: File): Promise<ParsedSheet> {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: "array" });
  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  if (!sheet) return { headers: [], rows: [] };

  const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { defval: "" });
  const headers = rows.length ? Object.keys(rows[0]) : [];
  return { headers, rows };
}

const EMAIL_HINTS = ["email", "e-mail", "correo", "mail"];
const POINTS_HINTS = ["points", "puntos", "score", "puntaje", "puntuación", "puntuacion", "pts", "total"];
const NAME_HINTS = ["name", "nombre", "player", "jugador", "participant", "participante", "member", "miembro", "user", "usuario"];

export interface SheetColumns {
  name: string;
  /** Empty when the file has no email column. */
  email: string;
  points: string;
}

export function detectColumns(sheet: ParsedSheet): SheetColumns {
  const taken = new Set<string>();

  const byHint = (hints: string[]) =>
    sheet.headers.find((header) => {
      const key = header.toLowerCase().trim();
      return !taken.has(header) && hints.some((hint) => key === hint || key.includes(hint));
    });

  const share = (header: string, test: (value: unknown) => boolean) => {
    const values = sheet.rows.map((row) => row[header]).filter((v) => String(v).trim() !== "");
    if (!values.length) return 0;
    return values.filter(test).length / values.length;
  };
  const looksNumeric = (v: unknown) => Number.isFinite(parsePoints(v));
  const looksLikeEmail = (v: unknown) => EMAIL_PATTERN.test(String(v).trim());

  const email =
    byHint(EMAIL_HINTS) ?? sheet.headers.find((h) => share(h, looksLikeEmail) >= 0.8) ?? "";
  if (email) taken.add(email);

  const points =
    byHint(POINTS_HINTS) ??
    sheet.headers.find((h) => !taken.has(h) && share(h, looksNumeric) >= 0.8) ??
    sheet.headers.find((h) => !taken.has(h) && h !== sheet.headers[0]) ??
    "";
  if (points) taken.add(points);

  const name =
    byHint(NAME_HINTS) ??
    sheet.headers.find((h) => !taken.has(h) && share(h, looksNumeric) < 0.5) ??
    sheet.headers.find((h) => !taken.has(h)) ??
    "";

  return { name, email, points };
}

export interface SkippedRow {
  row: number;
  reason: string;
}

export function buildEntries(
  sheet: ParsedSheet,
  columns: SheetColumns,
): { entries: LeaderboardEntry[]; skipped: SkippedRow[] } {
  const entries: LeaderboardEntry[] = [];
  const skipped: SkippedRow[] = [];

  sheet.rows.forEach((row, index) => {
    // Row 1 is the header in the spreadsheet the admin is looking at.
    const rowNumber = index + 2;
    const name = String(row[columns.name] ?? "").trim();
    const email = columns.email ? String(row[columns.email] ?? "").trim().toLowerCase() : "";
    const rawPoints = String(row[columns.points] ?? "").trim();
    const points = parsePoints(row[columns.points]);

    if (!name && !email && rawPoints === "") return;
    if (!name) {
      skipped.push({ row: rowNumber, reason: "Missing name" });
      return;
    }
    if (email && !EMAIL_PATTERN.test(email)) {
      skipped.push({ row: rowNumber, reason: `"${email}" is not a valid email` });
      return;
    }
    if (!Number.isFinite(points)) {
      skipped.push({ row: rowNumber, reason: rawPoints ? `"${rawPoints}" is not a number` : "Missing points" });
      return;
    }
    entries.push(email ? { name, email, points } : { name, points });
  });

  return { entries: rankEntries(entries).map(({ rank: _rank, ...entry }) => entry), skipped };
}

const TEMPLATE_ROWS = [
  { Name: "Oliver Overhill", Email: "oliver@example.org", Points: 2815 },
  { Name: "Rach LB", Email: "rach@example.org", Points: 2772 },
  { Name: "Luiza Bitina", Email: "luiza@example.org", Points: 2670 },
];

export function downloadTemplate(format: "xlsx" | "csv"): void {
  downloadRows(TEMPLATE_ROWS, `leaderboard-template.${format}`, format);
}

export function exportEntries(entries: LeaderboardEntry[], format: "xlsx" | "csv"): void {
  const rows = rankEntries(entries).map((e) => ({
    Rank: e.rank,
    Name: e.name,
    Email: e.email ?? "",
    Points: e.points,
  }));
  downloadRows(rows, `leaderboard.${format}`, format);
}

function downloadRows(rows: Record<string, unknown>[], fileName: string, format: "xlsx" | "csv"): void {
  const sheet = XLSX.utils.json_to_sheet(rows);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, sheet, "Leaderboard");
  XLSX.writeFile(workbook, fileName, { bookType: format });
}
